import { useEffect, useRef, useState } from "react";
import { getRainConfidence } from "@/lib/api";

const REFRESH_MS = 5 * 60_000; // 5min — same cadence as the forecast hook
const IDLE_PAUSE_MS = 5 * 60_000;

/**
 * useRainConfidence — polls /api/rain-confidence for the current location.
 * Mirrors the behaviour of useLiveFeed (visibility + idle pause). Backend
 * caches the response, so calling this alongside useLiveFeed adds at most
 * a few KB of duplicate network for the same daily quota.
 */
export default function useRainConfidence(location) {
  const [confidence, setConfidence] = useState(null);
  const [error, setError] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!location?.latitude || !location?.longitude) return;
    // Clear stale data so a failing refresh doesn't leave the previous
    // location's score visible under the new location's name.
    setConfidence(null);
    setError(null);
    let timer;

    const refresh = async () => {
      try {
        const r = await getRainConfidence(location.latitude, location.longitude);
        if (cancelledRef.current) return;
        setConfidence(r);
        setError(null);
      } catch (e) {
        if (!cancelledRef.current) setError(e?.message || "fetch failed");
      }
    };

    refresh();
    timer = setInterval(() => {
      if (document.hidden) return;
      if (Date.now() - lastActivityRef.current > IDLE_PAUSE_MS) return;
      refresh();
    }, REFRESH_MS);

    const bumpActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const onVis = () => {
      if (!document.hidden) {
        lastActivityRef.current = Date.now();
        refresh();
      }
    };
    window.addEventListener("touchstart", bumpActivity, { passive: true });
    window.addEventListener("click", bumpActivity, { passive: true });
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelledRef.current = true;
      clearInterval(timer);
      window.removeEventListener("touchstart", bumpActivity);
      window.removeEventListener("click", bumpActivity);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [location?.latitude, location?.longitude]);

  return { confidence, error };
}
